import { fromJS, List } from 'immutable';

import Account from 'models/AccountModel';
import Snapshot from 'models/SnapshotModel';
import { InsightsGenerated } from 'enums/sse';

import {
  removeClientIntegration,
  snapshot,
  snapshotAccounts,
  deleteCreditLines,
  CLOSE_HEALTH_REPORT,
  CLOSE_BANK_DATA_LOADING_ERROR_MODAL,
  CLOSE_BANK_DATA_LOADING_MODAL,
  CLOSE_CLIENT_PAGE,
  CLOSE_INVITE_CLIENT_MODAL,
  OPEN_HEALTH_REPORT,
  OPEN_BANK_DATA_LOADING_ERROR_MODAL,
  OPEN_BANK_DATA_LOADING_MODAL,
  OPEN_INVITE_CLIENT_MODAL,
  REMOVE_SNAPSHOT,
  SET_MONITOR_CLIENT_BALANCE_SHEET_VIEW_TYPE,
  SET_MONITOR_CLIENT_BALANCE_SHEET_VIEW_TYPE_SUCCESS,
  SET_CURRENT_MONITOR_PAGE_CLIENT_ID,
  SET_MONITOR_PAGE_TAB,
  SET_NEW_EDIT_FLOW_CLIENT_DATA,
  SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS,
  SET_SNAPSHOT_CREATING,
} from './types';

export * from './actions';

const initialState = fromJS({
  network: {
    loading: false,
    error: false,
    creating: false,
  },
  loadingInsights: false,
  isHealthReportOpen: false,
  isDataLoadingModalOpen: false,
  isErrorLoadingModalOpen: false,
  isInvitationToBankingSolutionModalOpen: false,
  balanceSheetView: null,
  balanceSheetViewClassification: null,
  page: {
    id: 0,
    activeTab: 2,
  },
  byId: {},
});

const setHealthReport = (state, status) => state.set('isHealthReportOpen', status);

const setNewMonitorEditFlow = (state, status) => state.set('isNewMonitorEditFlow', status);

const openMonitorBankDataLoadingModal = (state) => state.set('isDataLoadingModalOpen', true);
const closeMonitorBankDataLoadingModal = (state) => state.set('isDataLoadingModalOpen', false);

const openMonitorBankDataLoadingErrorModal = (state) => state.set('isErrorLoadingModalOpen', true);
const closeMonitorBankDataLoadingErrorModal = (state) =>
  state.set('isErrorLoadingModalOpen', false);

const setCurrentMonitorClientId = (state, action) => state.setIn(['page', 'id'], `${action.id}`);
const setMonitorClientPageTab = (state, action) =>
  state.setIn(['page', 'activeTab'], action.number);
const closeMonitorClientPage = () => initialState;

const openInviteClientToBankingSolutionModal = (state) =>
  state.set('isInvitationToBankingSolutionModalOpen', true);
const closeInviteClientToBankingSolutionModal = (state) =>
  state.set('isInvitationToBankingSolutionModalOpen', false);

const setMonitorClientBalanceSheetViewType = (state, action) =>
  state.withMutations((s) =>
    s
      .set('loadingInsights', true)
      .set('balanceSheetView', action.view)
      .set('balanceSheetViewClassification', action.classification),
  );

const setMonitorClientBalanceSheetViewTypeSuccess = (state) => state.set('loadingInsights', false);

const handleSseUpdateSnapshotGenerationProgress = (state, action) => {
  const finished = action?.payload?.finished;
  const message = action?.payload?.message;

  if (!finished || message !== InsightsGenerated) {
    return state;
  }

  return setMonitorClientSnapshotRequest(state);
};

const setMonitorClientSnapshotRequest = (state) => state.setIn(['network', 'loading'], true);

const setMonitorClientSnapshotSuccess = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['network', 'loading'], false)
      .setIn(['network', 'error'], false)
      .mergeIn(['byId', action.response?.entityId], Snapshot.deserialize(action.response)),
  );

const setMonitorClientSnapshotAccountsSuccess = (state, action) =>
  state.setIn(
    ['byId', action.entityId.toString(), 'accounts'],
    List(action.response.map(Account.deserialize)),
  );

const deleteCreditLineSuccess = (state, action) =>
  state.updateIn(['byId', action.entityId, 'providerCreditLines'], (list) =>
    list.filter((creditLine) => creditLine.id !== action.creditLineId),
  );

const deleteCreditLineError = (state, action) => state.setIn(['network', 'error'], action.error);

const setMonitorClientSnapshotError = (state, action) =>
  state.withMutations((s) =>
    s.setIn(['network', 'loading'], false).setIn(['network', 'error'], action.error),
  );

const removeIntegration = (state, action) =>
  state.withMutations((currentState) =>
    currentState.setIn(['byId', String(action.id), 'integrations', 'privateAccounting'], ''),
  );

const setMonitorLoading = (state, action) => state.setIn(['network', 'creating'], action.isLoading);

const removeSnapshot = (state, action) => state.deleteIn(['byId', action.id]);

export default function monitorClientReducer(state = initialState, action) {
  switch (action.type) {
    case SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS:
      return handleSseUpdateSnapshotGenerationProgress(state, action);

    case snapshot.request:
      return setMonitorClientSnapshotRequest(state, action);
    case snapshot.success:
      return setMonitorClientSnapshotSuccess(state, action);
    case snapshot.failure:
      return setMonitorClientSnapshotError(state, action);
    case SET_SNAPSHOT_CREATING:
      return setMonitorLoading(state, action);

    case deleteCreditLines.success:
      return deleteCreditLineSuccess(state, action);
    case deleteCreditLines.failure:
      return deleteCreditLineError(state, action);

    case removeClientIntegration:
      return removeIntegration(state, action);

    case snapshotAccounts.success:
      return setMonitorClientSnapshotAccountsSuccess(state, action);

    case OPEN_BANK_DATA_LOADING_MODAL:
      return openMonitorBankDataLoadingModal(state);
    case CLOSE_BANK_DATA_LOADING_MODAL:
      return closeMonitorBankDataLoadingModal(state);

    case OPEN_HEALTH_REPORT:
      return setHealthReport(state, true);
    case CLOSE_HEALTH_REPORT:
      return setHealthReport(state, false);

    case SET_NEW_EDIT_FLOW_CLIENT_DATA:
      return setNewMonitorEditFlow(state, true);

    case OPEN_BANK_DATA_LOADING_ERROR_MODAL:
      return openMonitorBankDataLoadingErrorModal(state);
    case CLOSE_BANK_DATA_LOADING_ERROR_MODAL:
      return closeMonitorBankDataLoadingErrorModal(state);

    case CLOSE_CLIENT_PAGE:
      return closeMonitorClientPage();
    case SET_CURRENT_MONITOR_PAGE_CLIENT_ID:
      return setCurrentMonitorClientId(state, action);
    case SET_MONITOR_PAGE_TAB:
      return setMonitorClientPageTab(state, action);

    case CLOSE_INVITE_CLIENT_MODAL:
      return closeInviteClientToBankingSolutionModal(state);
    case OPEN_INVITE_CLIENT_MODAL:
      return openInviteClientToBankingSolutionModal(state);

    case SET_MONITOR_CLIENT_BALANCE_SHEET_VIEW_TYPE:
      return setMonitorClientBalanceSheetViewType(state, action);
    case SET_MONITOR_CLIENT_BALANCE_SHEET_VIEW_TYPE_SUCCESS:
      return setMonitorClientBalanceSheetViewTypeSuccess(state, action);

    case REMOVE_SNAPSHOT:
      return removeSnapshot(state, action);

    default:
      return state;
  }
}
